const cloneDeepPreservingFunctions = <T>(
  value: T,
  seen = new WeakMap<object, unknown>()
): T => {
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  if (typeof value === 'function') {
    return value;
  }

  if (seen.has(value)) {
    return seen.get(value) as T;
  }

  if (Array.isArray(value)) {
    const result: unknown[] = [];
    seen.set(value, result);

    value.forEach((item, index) => {
      result[index] = cloneDeepPreservingFunctions(item, seen);
    });

    return result as T;
  }

  const result: Record<string, unknown> = {};
  seen.set(value, result);

  Object.entries(value).forEach(([key, nestedValue]) => {
    result[key] = cloneDeepPreservingFunctions(nestedValue, seen);
  });

  return result as T;
};

export const omitDeep = <T extends Record<string, any>>(
  obj: T,
  paths: string[]
): T => {
  const result = cloneDeepPreservingFunctions(obj);

  for (const path of paths) {
    const keys = path.split('.');
    let node: Record<string, any> | undefined = result;

    for (let i = 0; i < keys.length - 1; i++) {
      const nextNode = node?.[keys[i]];

      if (typeof nextNode !== 'object' || nextNode === null) {
        node = undefined;
        break;
      }

      node = nextNode as Record<string, any>;
    }

    if (node != null) {
      delete node[keys[keys.length - 1]];
    }
  }

  return result;
};
